import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import styled from 'styled-components';
import jwt_decode from 'jwt-decode';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import { useRolePlay } from './graphql/useRolePlay';
import {
  type Delegation,
  TokenType,
  useAuth,
  type User,
  type UserInfo,
} from '../../../contexts';
import {
  Alert,
  type AlertDetails,
  Button,
  Checkbox,
  ROLES_MODULE,
  TextField,
} from '../../../components';
import { ROLES_SWITCH_BAR_TEXT } from '../../../components/RoleSwitchBar/lang';

type FormikVal = {
  netId: string;
  firstName: string;
  lastName: string;
  title: string;
  department: string;
  roles: Partial<Record<string, boolean>>;
  email: string;
  currentRole: string;

  sub?: string;
  username?: string;
  userId?: string;
  userInfo?: UserInfo;
  delegation?: Delegation[];
  rightFRCDepartmentChairList?: string[];
  role?: string[];
  permissions?: string[];
  iat?: number;
  exp?: number;
  iss?: string;
};
export const MockUser: React.FC = () => {
  const { user, setUser, role, setCurrentRole } = useAuth();
  const navigate = useNavigate();
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const getRoleMap = (): Partial<Record<string, boolean>> => {
    const roleMap: Partial<Record<string, boolean>> = {};
    Object.keys(ROLES_MODULE).forEach((key: string): void => {
      roleMap[ROLES_MODULE[key as keyof typeof ROLES_MODULE]] =
        user?.role?.includes(ROLES_MODULE[key as keyof typeof ROLES_MODULE]) ??
        false;
    });
    return roleMap;
  };
  const displayName = user?.userInfo?.displayName ?? '';
  const displayFirstName = displayName?.replace(/^\S+\s/, '') ?? '';
  const displayNameRemainingName = displayName?.split(' ')?.[0] ?? '';

  const initialValues: FormikVal = {
    netId: '',
    firstName: displayFirstName,
    lastName: displayNameRemainingName,
    title: '',
    department: '',
    roles: getRoleMap(),
    delegation: [],
    email: user?.username ?? '',
    currentRole: role ?? '',
    rightFRCDepartmentChairList: [],
    ...user?.userInfo,
  };

  const encodeJwt = (payload: any): string => {
    const header = {
      alg: 'HS256',
      typ: 'JWT',
    };

    const secret = 'front-end-only';

    const encodedHeader = btoa(JSON.stringify(header));
    const encodedPayload = btoa(JSON.stringify(payload));

    const signature = btoa(encodedHeader + '.' + encodedPayload + secret);

    return (encodedHeader + '.' + encodedPayload + '.' + signature).replaceAll(
      '=',
      '',
    );
  };

  const getRoles = (roles: Partial<Record<string, boolean>>): string[] => {
    const roleArray: string[] = [];
    Object.keys(roles).forEach((key: string): void => {
      if (roles[key]) {
        roleArray.push(key);
      }
    });
    return roleArray;
  };

  const formik = useFormik({
    initialValues,
    onSubmit: values => {
      const user: User = {
        sub: values.netId,
        username: values.email,
        userId: values.netId,
        tokenType: TokenType.INTERNAL,
        userInfo: {
          username: values.email,
          netId: values.netId,
          displayName: `${values.lastName.toUpperCase()}, ${values.firstName}`,
          title: values.title,
          department: values.department,
          rightFRCDepartmentChairList: values.rightFRCDepartmentChairList,
          createdAt: new Date().toDateString(),
          updatedAt: new Date().toDateString(),
        },
        delegation: values.delegation ?? [],
        role: getRoles(values.roles),
        permissions: [],
        iat: 10000000000,
        exp: 20000000000,
        iss: 'Front-end-only',
      };
      setUser?.(user);
      localStorage.setItem('tokens', encodeJwt(user));
      setCurrentRole(values.currentRole);
    },
  });

  if (process.env.REACT_APP_LOCAL_MODE == null) {
    navigate('/login');
  }

  /**
   * update the user information when used search and fill functionality
   */

  const makeupRoles = (role: string[]): Record<string, boolean> => {
    const roleMap: Record<string, boolean> = {};
    role.forEach((key: string): void => {
      roleMap[key] = true;
    });
    return roleMap;
  };

  const handleDataUpdate = async (accessTK: string): Promise<void> => {
    const userData: User = jwt_decode(accessTK);

    const email = userData?.userInfo?.email ?? ' ';
    const fullName = userData?.userInfo?.displayName ?? ' ';
    const firstName = fullName?.replace(/^\S+\s/, '');
    const remainingName = fullName?.split(' ')[0];
    // update the formik values
    await formik.setValues({
      ...formik.values,
      ...userData?.userInfo,
      email,
      firstName,
      lastName: remainingName,
      delegation: (userData?.delegation as Delegation[]) ?? [],
      roles: makeupRoles(userData?.role),
      currentRole: (userData?.role)[0],
    });
    await formik.validateForm();
  };

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleStartRolePlay = async (): Promise<void> => {
    const searchId = formik.values.netId ?? '';
    try {
      const accessTK = await useRolePlay(client, searchId);
      const getTk = accessTK?.accessTK;
      if (getTk !== null) {
        await handleDataUpdate(getTk);
        localStorage.setItem('tokens', getTk); // set the tk
        const newUser: User = jwt_decode(getTk);
        setUser?.(newUser);
        setAlertDetails({
          alertContent: `You are ${searchId} now.`,
          alertType: 'success',
        });
      } else {
        setAlertDetails({
          alertContent: 'No matching academic staff found.',
          alertType: 'warning',
        });
      }
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
    } finally {
      setOpenAlert(true);
    }
  };

  return (
    <Container>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: '240px',
          margin: '2rem',
          padding: '1rem',
          border: '1px solid black',
          borderRadius: '5px',
        }}
      >
        <Alert
          openAlert={openAlert}
          alertDetails={alertDetails}
          setOpenAlert={setOpenAlert}
        />
        <form onSubmit={formik.handleSubmit}>
          <h1>Role List</h1>
          {Object.keys(ROLES_MODULE).map((key: string): React.ReactNode => {
            return (
              <div key={key} className="!w-full">
                {key !== 'NA' && (
                  <Checkbox
                    name={`roles.${
                      ROLES_MODULE[key as keyof typeof ROLES_MODULE]
                    }`}
                    label={ROLES_SWITCH_BAR_TEXT.NAMING[key]}
                    value={
                      formik.values.roles[
                        ROLES_MODULE[key as keyof typeof ROLES_MODULE]
                      ] ?? false
                    }
                    onChange={formik.handleChange}
                  />
                )}
              </div>
            );
          })}
        </form>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          margin: '2rem',
          padding: '1rem',
          border: '1px solid black',
          borderRadius: '5px',
          minWidth: '400px',
        }}
      >
        <form onSubmit={formik.handleSubmit} noValidate>
          <h1>Role Play</h1>
          <Grid className="mt-2">
            <TextField
              id="netId"
              name="netId"
              label="Net ID"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              onChange={formik.handleChange}
              value={formik.values.netId}
            />
          </Grid>
          <Grid className="mt-2">
            <TextField
              id="email"
              name="email"
              label="Email"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              onChange={formik.handleChange}
              value={formik.values.email}
            />
          </Grid>
          <Grid className="mt-2">
            <TextField
              id="firstName"
              name="firstName"
              label="First Name"
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
              onChange={formik.handleChange}
              value={formik.values.firstName}
            />
          </Grid>
          <Grid className="mt-2">
            <TextField
              id="lastName"
              name="lastName"
              label="Last Name"
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
              onChange={formik.handleChange}
              value={formik.values.lastName}
            />
          </Grid>
          <Grid className="mt-2">
            <TextField
              id="title"
              name="title"
              label="Title"
              fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={formik.handleChange}
              value={formik.values.title}
            />
          </Grid>
          <Grid className="mt-2">
            <TextField
              id="department"
              name="department"
              label="Department"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              onChange={formik.handleChange}
              value={formik.values.department}
            />
          </Grid>
          <Grid className="mt-2">
            <TextField
              id="currentRole"
              name="currentRole"
              label="Current Role"
              fullWidth
              InputLabelProps={{ shrink: true }}
              onChange={formik.handleChange}
              value={formik.values.currentRole}
            />
          </Grid>
          <div className="mt-4 flex flex-col gap-[1rem]">
            <div>
              <Button
                type="button"
                variant="contained"
                onClick={handleStartRolePlay}
                sx={{ textTransform: 'none' }}
              >
                Start Role Play
              </Button>
            </div>
            <div>
              <Button type="submit" sx={{ textTransform: 'none' }}>
                Submit (Only effective in DEV)
              </Button>
            </div>
          </div>
        </form>
      </div>

      <div
        style={{
          maxWidth: '1000px',
          margin: '2rem',
          padding: '1rem',
          border: '1px solid black',
          borderRadius: '5px',
          minWidth: '400px',
        }}
      >
        <h1>Mock User</h1>
        <UserDetailInfo>
          <div>Token:</div>
          <div
            title={
              window.localStorage.getItem('tokens') ?? 'No Available Token'
            }
          >
            Hover Me
          </div>
          <div>Cookie:</div>
          <div title={window.document.cookie}>Hover Me</div>
          <div>User Info:</div>
          <div title={JSON.stringify(user)}>Hover Me</div>
          <div>Current Role:</div>
          <div>{role}</div>
          <div>Roles:</div>
          <div>{formik.values.role?.map(item => item + ', ')}</div>
          <div>FRC Department Chair List:</div>
          <div>
            {formik.values.rightFRCDepartmentChairList?.map(
              item => item + ', ',
            )}
          </div>
        </UserDetailInfo>
      </div>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;

  h1 {
    text-align: left;
    font-size: 1rem;
    font-weight: 500;

    margin-bottom: 1rem;
  }
`;

const UserDetailInfo = styled.div`
  word-break: break-all;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  div {
    width: calc(50% - 4px);
  }
`;
